import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { showTranslated, translate } from "../config/translator";
import { useAnchoredOverlay } from "../views/components/ClavaDropdown";
import { AreaOfInterest, AreaOfInterestService, CountryCode, Region, RegionService } from "../client/api";
import { changeAoi } from "../store/actions/userActions";
import { pathHome } from "../config/routes";
import { useStableId } from "./useStableId";
import RoundedRectangle from "../views/components/RoundedRectangle";
import GridRectangle, { GridRectangleItem } from "../views/components/GridRectangle";
import ProgressIndicator from "../views/components/ProgressIndicator";
import { AS_AOI_SELECTED } from "../config/constants";
import { sortAlphabetically } from "../config/utils";
import { ClavaContext } from "../config/contexts";


export const hasUserSelectedAoi = (): boolean => {
  const aoiSelected = localStorage.getItem(AS_AOI_SELECTED);

  if (aoiSelected === "true") {
    return true;
  }

  if (aoiSelected === "false") {
    return false;
  }


  if (aoiSelected === null) {
    localStorage.setItem(AS_AOI_SELECTED, "true");
    return true;
  }

  return false;
};

export const markAoiAsSelected = (): void => {
  localStorage.setItem(AS_AOI_SELECTED, "true");
};

export const markNewUserNeedsAoiSelection = (): void => {
  if (localStorage.getItem(AS_AOI_SELECTED) === null) {
    localStorage.setItem(AS_AOI_SELECTED, "false");
  }
};

let globalAutoOpenConsumed = false;

export type AoiSelectorItem<T extends string | number> = {
  key: T;
  label: string;
};

export type UseAoiSelectorProps<T extends string | number> = {
  onChange: (val: T | undefined) => void;
  value?: T;
  placeholder?: string;
  disabled?: boolean;
  withoutChoose?: boolean;
  items: AoiSelectorItem<T>[];
  autoOpen?: boolean;
  transparent?: boolean;
  primary?: boolean;
  className?: string;
  iconL?: FontAwesomeIconProps["icon"];
};

export type UseAoiSelectorReturn = {
  isOpen: boolean;
  rect: DOMRect | null;
  regions: Region[];
  regionsLoading: boolean;
  aois: AreaOfInterest[];
  aoisLoading: boolean;
  selectedAoi: AreaOfInterest | undefined;
  selectedRegion: Region | undefined;
  selectedRegionId: number | undefined;
  step: 0 | 1;
  dropdownId: string;
  containerRef: React.RefObject<HTMLDivElement>;
  realPlaceholder: string;
  inputClass: string;
  realValue: string | undefined;
  regionItems: GridRectangleItem[];
  aoiItems: GridRectangleItem[];
  overlayStyle: React.CSSProperties | null;
  dropdownOverlay: React.ReactNode;
  onOpen: (e: React.MouseEvent) => void;
  setIsOpen: (value: boolean) => void;
  setRect: (value: DOMRect | null) => void;
  setStep: (value: 0 | 1) => void;
};

export function useAoiSelector<T extends string | number>({
                                                            onChange,
                                                            value = undefined,
                                                            placeholder = undefined,
                                                            disabled = undefined,
                                                            withoutChoose = undefined,
                                                            items,
                                                            autoOpen,
                                                            transparent = undefined,
                                                            primary = undefined,
                                                            className,
                                                            iconL = undefined
                                                          }: UseAoiSelectorProps<T>): UseAoiSelectorReturn {
  const { l } = useContext(ClavaContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [aois, setAois] = useState<AreaOfInterest[]>([]);
  const [aoisLoading, setAoisLoading] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState<number | undefined>(
    undefined
  );
  const [step, setStep] = useState<0 | 1>(0);
  const dropdownId = useStableId("aoi-selector");

  const realPlaceholder = useMemo(() => {
    if (!placeholder) return withoutChoose ? "" : translate("select", l);
    return placeholder;
  }, [l, placeholder, withoutChoose]);

  const realValue = useMemo(
    () => items.find((it) => it.key === value)?.label,
    [items, value]
  );
  const regionItems = useMemo(
    (): GridRectangleItem[] => {
      const mappedItems = regions.map((region) => ({
        id: region.id,
        label: showTranslated(region.name, l),
        image: region.image,
        onClick: () => {
          if (region.id === 1 || region.id === 2) {
            const aoiId = region.areaOfInterestIds?.[0];
            if (aoiId) {
              const selectedAoiItem = aois.find((a) => a.id === aoiId);
              if (selectedAoiItem) {
                markAoiAsSelected();
                changeAoi(dispatch, selectedAoiItem);
                const match = items.find(
                  (it) => String(it.key) === String(selectedAoiItem.id)
                );
                onChange(match ? match.key : undefined);
                setIsOpen(false);
                setRect(null);
                navigate(pathHome());
                return;
              }
            }
            setSelectedRegionId(region.id);
            setStep(1);
            return;
          }
          setSelectedRegionId(region.id);
          setStep(1);
        }
      }));
      return sortAlphabetically(mappedItems, (item) => item.label);
    },
    [regions, l, aois, items, onChange, dispatch, navigate]
  );

  const aoiItems = useMemo(
    (): GridRectangleItem[] => {
      const mappedItems = aois
        .filter((a) => a.regionId === selectedRegionId)
        .map((aoiItem) => ({
          id: aoiItem.id,
          label: showTranslated(aoiItem.name, l),
          onClick: () => {
            markAoiAsSelected();
            changeAoi(dispatch, aoiItem);
            const match = items.find((it) => String(it.key) === String(aoiItem.id));
            onChange(match ? match.key : undefined);
            setIsOpen(false);
            setRect(null);
            navigate(pathHome());
          }
        }));
      return sortAlphabetically(mappedItems, (item) => item.label);
    },
    [aois, selectedRegionId, l, items, onChange, dispatch, navigate]
  );

  const loadDataIfNeeded = useCallback(() => {
    if (regions.length === 0 && !regionsLoading) {
      setRegionsLoading(true);
      RegionService.getRegionsRegionGet(CountryCode.IT)
        .then((regionList) => {
          setRegions(regionList);
          setRegionsLoading(false);
        })
        .catch(() => {
          setRegionsLoading(false);
        });
    }
    if (aois.length === 0 && !aoisLoading) {
      setAoisLoading(true);
      AreaOfInterestService.getAreaOfInterestsAreaOfInterestGet()
        .then((list) => {
          setAois(list);
          setAoisLoading(false);
        })
        .catch(() => setAoisLoading(false));
    }
  }, [regions.length, regionsLoading, aois.length, aoisLoading]);

  useEffect(() => {
    if ((value !== undefined && value !== null) && (regions.length === 0 || aois.length === 0)) {
      loadDataIfNeeded();
    }
  }, [value, regions.length, aois.length, loadDataIfNeeded]);

  const resetSelection = useCallback(() => {
    setStep(0);
    setSelectedRegionId(undefined);
  }, []);

  const onOpen = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      if (isOpen) {
        setIsOpen(false);
        setRect(null);
        return;
      }
      const clicked = e.target as HTMLElement;
      const container =
        (clicked.closest(`#${dropdownId}`) as HTMLElement) ||
        containerRef.current;
      if (!container) return;
      setRect(container.getBoundingClientRect());
      setIsOpen(true);
      loadDataIfNeeded();
      resetSelection();
    },
    [disabled, isOpen, loadDataIfNeeded, resetSelection, dropdownId]
  );

  useEffect(() => {
    if (autoOpen && !isOpen && !hasUserSelectedAoi() && !globalAutoOpenConsumed) {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const r = el.getBoundingClientRect();
      const isVisible = r.width > 0 && r.height > 0;
      if (!isVisible) {
        return;
      }
      globalAutoOpenConsumed = true;
      setRect(r);
      setIsOpen(true);
      loadDataIfNeeded();
      resetSelection();
    }
  }, [autoOpen, isOpen, loadDataIfNeeded, resetSelection]);

  const setRectForOverlay = useCallback(
    (r: DOMRect | false) => {
      if (r === false) {
        setIsOpen(false);
        setRect(null);
      } else {
        setRect(r);
        setIsOpen(true);
      }
    },
    []
  );

  useAnchoredOverlay(
    isOpen && rect ? rect : false,
    setRectForOverlay,
    dropdownId
  );

  const overlayStyle = useMemo(() => {
    if (!isOpen || !rect) return null;

    const overlayWidth = Math.min(1320, window.innerWidth - 32);
    const overlayLeft = (() => {
      if (window.innerWidth >= 1024) {
        return Math.max(16, (window.innerWidth - overlayWidth) / 2);
      }
      const centeredLeft = rect.left + rect.width / 2 - overlayWidth / 2;
      const minLeft = 16;
      const maxLeft = window.innerWidth - overlayWidth - 16;
      return Math.max(minLeft, Math.min(centeredLeft, maxLeft));
    })();

    return {
      top: rect.bottom + window.scrollY,
      left: overlayLeft,
      width: overlayWidth
    };
  }, [isOpen, rect]);

  const inputClass = useMemo(() => {
    let style = "w-full outline-0 rounded-xl";
    if (!transparent) {
      style +=
        " active:bg-input-bg-active active:dark:bg-input-bg-active-dark focus:bg-input-bg-active focus:dark:bg-input-bg-active-dark border active:dark:border-input-border-active-dark focus:dark:border-input-border-active-dark py-2";
      if (disabled) {
        style += " border-input-border opacity-60";
      } else {
        style +=
          " bg-input-bg dark:bg-input-bg-dark border-input-border dark:border-input-border-dark active:border-input-border-active focus:border-input-border-active";
      }
    }
    if (value) {
      if (primary) {
        style += " !text-primary";
      } else {
        style += " text-font dark:text-font-dark";
      }
    } else {
      style += " text-placeholder dark:text-placeholder-dark";
    }
    if (iconL) {
      style += " px-8";
    } else {
      style += " pl-2 pr-8";
    }
    return className ? `${style} ${className}` : style;
  }, [disabled, iconL, primary, transparent, value, className]);

  const dropdownOverlay = useMemo((): React.ReactNode => {
    if (!isOpen || !rect || !overlayStyle) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Close dropdown"
          className="fixed inset-0 z-[100] backdrop-blur backdrop-brightness-90"
          onClick={() => {
            setIsOpen(false);
            setRect(null);
          }}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" ||
              e.key === " " ||
              e.key === "Escape"
            ) {
              setIsOpen(false);
              setRect(null);
            }
          }}
        />
        {/* Dropdown content */}
        <div
          id="clava-dropdown-overlay"
          className="absolute z-[101]"
          style={overlayStyle}
        >
          <RoundedRectangle
            className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark shadow-card dark:shadow-card-dark"
            hasBorder
            hasTitle
            titleType="upLeft"
            titleKey={step === 0 ? "selectRegion" : "selectProvince"}
          >
            <div className="p-6 pt-4 flex flex-col w-full max-h-[80vh]">
              <div className="flex items-center mb-4">
                {step === 1 && (
                  <button
                    type="button"
                    aria-label={translate("back", l)}
                    className="mr-3 p-2 rounded-md hover:bg-input-bg dark:hover:bg-input-bg-dark focus:outline-none focus:ring-2 focus:ring-primary"
                    onClick={() => setStep(0)}
                  >
                    <FontAwesomeIcon
                      icon={faChevronLeft}
                      className="w-4 h-4 text-font dark:text-font-dark"
                    />
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto">
                <GridRectangle
                  items={step === 0 ? regionItems : aoiItems}
                  columns={{
                    mobile: 1,
                    tablet: 2,
                    desktop: 3
                  }}
                  loading={
                    (regionsLoading && step === 0) || (aoisLoading && step === 1)
                  }
                  loadingText={translate("loading", l)}
                  className={step === 0 ? "flex-1 p-1" : "p-1"}
                  showImages={step === 0}
                  imageWidth={48}
                />
              </div>
              <div
                className="pt-4 mt-4 border-t border-light-gray dark:border-light-gray-dark bg-bg-secondary dark:bg-bg-secondary-dark">
                <ProgressIndicator total={2} current={step} />
              </div>
            </div>
          </RoundedRectangle>
        </div>
      </>
    );
  }, [
    isOpen,
    rect,
    overlayStyle,
    step,
    l,
    regionItems,
    aoiItems,
    regionsLoading,
    aoisLoading,
    setIsOpen,
    setRect
  ]);

  const selectedAoi = useMemo(() => {
    if (value === undefined || value === null) return undefined;
    return aois.find((aoi) => String(aoi.id) === String(value));
  }, [aois, value]);

  const selectedRegion = useMemo(() => {
    if (!selectedAoi) return undefined;
    return regions.find((region) => region.id === selectedAoi.regionId);
  }, [regions, selectedAoi]);

  return {
    isOpen,
    rect,
    regions,
    regionsLoading,
    aois,
    aoisLoading,
    selectedAoi,
    selectedRegion,
    selectedRegionId,
    step,
    dropdownId,
    containerRef,
    realPlaceholder,
    inputClass,
    realValue,
    regionItems,
    aoiItems,
    overlayStyle,
    dropdownOverlay,
    onOpen,
    setIsOpen,
    setRect,
    setStep
  };
}

